import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import {
  updatePerson,
  updatePersonDespiteDuplicates,
  type Person,
  type PersonCard,
  type PersonEdit,
} from '@/api/people';
import { queryKeyRoots } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { DuplicateMatches } from '@/pages/Candidates/DuplicateMatches';
import {
  PERSON_FIELDS,
  personFieldsSchema,
  type PersonFieldsForm,
} from '@/pages/Candidates/personFields';
import { PersonFields } from '@/pages/Candidates/PersonFields';

interface Props {
  person: PersonCard;
  onClose: () => void;
}

/** The four editable fields of the card, as the form holds them. */
const fieldsOf = (person: PersonCard): PersonFieldsForm => ({
  full_name: person.full_name,
  email: person.email,
  phone: person.phone,
  source: person.source,
});

/**
 * **Only what changed, and never an explicit `null`.**
 *
 * `PersonUpdate` is *"every field optional, only the ones sent are written"* — and optional there
 * means **omittable, not nullable**: `{"full_name": null}` is a **422**, deliberately, so that a
 * `NULL` is never written onto a NOT NULL column. Every value here comes from the form as a string
 * (the schema trims it), so a `null` is not merely avoided but unproduceable; and sending only the
 * changed fields is what keeps an edit of the source from rewriting the three matching keys the
 * duplicate check reads (`Person.save()` recomputes them from whatever is written).
 */
function changesOf(person: PersonCard, submitted: PersonFieldsForm): PersonEdit {
  const before = fieldsOf(person);
  const changes: PersonEdit = {};

  for (const field of PERSON_FIELDS) {
    if (submitted[field] !== before[field]) changes[field] = submitted[field];
  }

  return changes;
}

/**
 * The three keys ADR 0014 matches on, of the four this form writes. `source` is not one of them: the
 * matcher never looks at it, so a corrected source cannot make a warning stale.
 */
const MATCH_KEYS = ['full_name', 'email', 'phone'] as const;

/**
 * §6.5's **Edit**, in the overflow menu — and §6.4's *"Add / edit candidate"* is one section on
 * purpose: it is the same four fields, the same schema, the same labels and the same field-error
 * sentences (`PersonFields`), so a person is described one way wherever they are written.
 *
 * ⚠️ **The vacancy is not here.** §6.4's form has a fifth field and this one must not: a vacancy
 * belongs to a consideration, and `PersonUpdate` deliberately carries no `role_id` — *"a status or a
 * vacancy on a person is ADR 0002 running backwards"*. Nothing on this card opens or moves a run
 * either (§6.5 specifies no such control).
 *
 * ⚠️ **`PATCH` answers `PersonOut`, which is not the card**: no derived values, no considerations, no
 * interviews. So the answer is thrown away and **both** families are invalidated — the person (this
 * card) and the considerations (§6.3's Candidate cell renders `person_full_name` and `person_source`,
 * so an edit that refreshed only the card would leave the list showing the old name until the tab is
 * closed).
 *
 * ⚠️ **This route has two 409s and they are told apart by shape, never by the code** (TASK-112).
 *
 * 1. **With `possible_duplicates`** — feedback protection #7 (ADR 0002 + ADR 0014) on the door a
 *    *create* is not the only one through: an edit can reach the duplicate a create refuses, by typing
 *    an existing candidate's email onto another card. It is the interaction §6.4 renders, not a
 *    failure, so it renders as the matches with **one secondary action** — *"Save the changes
 *    anyway"* — which is the only call that carries the acknowledgement
 *    (`updatePersonDespiteDuplicates`). There is no primary here: nothing on this card opens or moves
 *    a run, so §6.4's *"Add a new consideration to the existing card"* would be a button that cannot
 *    work.
 * 2. **Without it** — the anonymised refusal, said in the screen's own words. It is one of the six
 *    write paths that erasure closes (`services.refuse_if_anonymized`, ADR 0009). The menu does not
 *    offer Edit on an anonymised person at all (`CardMenu`), so reaching it means the erasure landed
 *    between the card being read and this form being submitted — an Admin working at the same time —
 *    and the answer is a sentence from the `candidates` namespace, never the server's English `detail`
 *    and never a status code (§8, §11). ⚠️ **BUG-033 put that refusal *before* the duplicate check on
 *    the server**, so this body is reachable for an anonymised person whose (blank) keys could never
 *    match anything, and the ordering must not be masked here by reading the status code first.
 *
 * ⚠️ **And there is no pre-check.** `POST /people/check-duplicates/` takes the three keys with no way
 * to exclude the row being edited, so it would report this card as its own duplicate.
 */
export function EditPersonDialog({ person, onClose }: Props) {
  const { t } = useTranslation(['candidates', 'common', 'components']);
  const queryClient = useQueryClient();

  const form = useForm<PersonFieldsForm>({
    resolver: zodResolver(personFieldsSchema),
    // The dialog is mounted when it opens and unmounted when it closes (`CardMenu`), so the card as
    // it is now *is* the default: no effect resets the form, and a stale draft cannot survive a
    // refetch that changed the person underneath it.
    defaultValues: fieldsOf(person),
  });

  const values = form.watch();

  /**
   * ⚠️ **The override is a property of the *call*, never of the form** (ADR 0014, and `PersonEdit`'s
   * own docblock): the flag is spelled in `updatePersonDespiteDuplicates` alone, and what travels
   * through this mutation is the boolean deciding **which helper** is called. A form field carrying it
   * would be one character away from being submitted as `true` on the first attempt, and then the
   * detection is decorative.
   */
  const save = useMutation<Person, ApiError, { changes: PersonEdit; acknowledge: boolean }>({
    mutationFn: ({ changes, acknowledge }) =>
      acknowledge
        ? updatePersonDespiteDuplicates(person.id, changes)
        : updatePerson(person.id, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.person });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.considerations });
      /**
       * The milder version of the staleness `AnonymizeDialog` carries the argument for (sprint-end
       * review, 2026-07-28): §6.4's pre-check is keyed on the three match keys **as they were typed**
       * and holds the matched person's own name, so after a rename it still answers *"Ada Lovelace
       * already exists"* about a card that no longer says that — with no request, so nothing on the
       * wire can correct it. Removed rather than invalidated, because the answer is about a name that
       * is nobody's now.
       */
      queryClient.removeQueries({ queryKey: queryKeyRoots.duplicateCheck });
      onClose();
    },
  });

  const attempt = async (submitted: PersonFieldsForm, acknowledge: boolean) => {
    const changes = changesOf(person, submitted);
    // Nothing was changed, so there is nothing to write: a PATCH with an empty body would answer 200,
    // touch `updated_at` and move this candidate to the top of §6.3's list for no reason at all.
    if (Object.keys(changes).length === 0) return onClose();

    try {
      await save.mutateAsync({ changes, acknowledge });
    } catch {
      // Held by the mutation and rendered below; rethrowing reaches only the console.
    }
  };

  const submit = form.handleSubmit((submitted) => attempt(submitted, false));

  /**
   * §6.5's **secondary** action, and the only call that carries ADR 0014's acknowledgement.
   *
   * It goes back through the form rather than replaying `save.variables`, so a source corrected while
   * the warning stood travels with it — the three **match** keys cannot have changed, because any
   * change to them drops the warning and this button with it (`answerIsCurrent` below).
   */
  const saveAnyway = form.handleSubmit((submitted) => attempt(submitted, true));

  /**
   * Whether the answer `save` holds is still about what is in the form — §6.4's `answerIsCurrent`
   * rule, on the other route.
   *
   * A 409 is an answer about the body that was sent. Once any of the three match keys changes, the
   * matches it carries describe a body nobody would send again, and leaving them up would go on
   * offering *"save the changes anyway"* about a name that has since been corrected. The attempted
   * keys are the card's own values with the attempted changes over them, because a `PATCH` sends only
   * what changed.
   */
  const attempted = save.variables?.changes;
  const attemptedKeys = attempted === undefined ? undefined : { ...fieldsOf(person), ...attempted };
  const answerIsCurrent =
    attemptedKeys !== undefined &&
    MATCH_KEYS.every((key) => attemptedKeys[key] === values[key].trim());

  /**
   * ⚠️ **The two 409s of this route are told apart by *shape*, never by the status code** — the rule
   * §6.4 already uses for the considerations route, and it is load-bearing here: BUG-033 put ADR 0009's
   * erasure refusal **before** the duplicate check inside `update_person`, so an anonymised person's
   * edit answers a 409 with no `possible_duplicates` and must go on rendering the erasure sentence
   * rather than an override this screen would otherwise offer for somebody else's refusal.
   */
  const warned = save.error?.bodyWith('possible_duplicates')?.possible_duplicates;
  const duplicates = answerIsCurrent ? (warned ?? []) : [];

  const failure = (() => {
    if (!save.error || warned) return null;
    // ⚠️ The **write** sentence, not §8's read banner (BUG-017): nothing queues this `PATCH`, so a
    // sentence promising it will be sent when the connection returns was false on this path.
    if (save.error.isOffline) return t('components:state.offlineWrite');
    if (save.error.isThrottled) return t('components:state.throttled');
    if (save.error.status === 409) return t('candidates:card.edit.anonymized');
    return t('candidates:card.edit.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('candidates:card.edit.title')}
      description={t('candidates:card.edit.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          {/*
           * While the matches stand the form's own submit is **withdrawn** (§6.4's rule, same reason):
           * it would send the body the server has just refused and answer 409 again — a button whose
           * only effect is to redraw the same list. The one action in that state is the secondary
           * below, and it lives beside the sentence it answers.
           */}
          {duplicates.length === 0 && (
            <Button onClick={() => void submit()} isLoading={save.isPending}>
              {t('candidates:card.edit.submit')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          <FormProvider {...form}>
            <PersonFields />
          </FormProvider>
        </form>

        {failure && (
          <p role="alert" className="text-sm text-negative">
            {failure}
          </p>
        )}

        {/*
         * ⚠️ **No pre-check on this dialog, and that is a decision rather than an omission.**
         * `POST /people/check-duplicates/` takes the three keys and has no way to exclude the row being
         * edited, so it would answer that this card is its own duplicate — §6.4's pre-check does not
         * port. The 409 is the whole mechanism here, which also makes it the enforcement point rather
         * than a warning that can lose a race.
         *
         * Below the fields, as §6.4's is: above them it would push the field under the cursor down the
         * dialog (§8: never a layout that jumps).
         */}
        {duplicates.length > 0 && (
          <DuplicateMatches
            duplicates={duplicates}
            heading={t('candidates:card.edit.duplicate.heading')}
          >
            {/*
             * §6.5's **single secondary, and no primary**: nothing on this card opens or moves a run
             * and `PersonUpdate` carries no `role_id`, so §6.4's *"Add a new consideration to the
             * existing card"* would be a button that cannot work here (TASK-112).
             */}
            <Button
              variant="secondary"
              size="sm"
              isLoading={save.isPending}
              onClick={() => void saveAnyway()}
            >
              {t('candidates:card.edit.duplicate.saveAnyway')}
            </Button>
          </DuplicateMatches>
        )}
      </div>
    </Dialog>
  );
}
